import { useEffect, useRef, useState } from "react";
import PageContainer from "@components/layout/PageContainer";
import { useParams } from "react-router-dom";
import ModelIndex, { ModelIndexHandle } from "@components/layout/ModelIndex";
import { faEdit } from "@fortawesome/free-solid-svg-icons";
import { formatMoneyValue } from "@utils/Currency";
import { ProviderInvoiceDataType } from "@/api/ProviderInvoiceApi";
import { Col, Container, Row } from "reactstrap";
import { formatDate } from "@utils/Api";
import LoadingScreen from "@components/content/LoadingScreen";
import { stripHtmlTags } from "@utils/Utils";
import { FetchResponse } from "@/api/ApiClient";
import { ProviderRecibeFormDataKeys } from "@/generated/ProviderRecibeFormDataKeys";
import { ProviderRecibeRelatedAttributes } from "@/formTypes/ProviderRecibeFormDataType";
import { ProviderRecibeApi, ProviderRecibeDataType } from "@/api/ProviderRecibeApi";
import { BuyInvoiceApi, BuyInvoiceDataType } from "@/api/BuyInvoiceApi";
import { PAYMENT_METHODS } from "@utils/Constants";

type ProviderRecibeDocumentRow = (ProviderInvoiceDataType | BuyInvoiceDataType) & {
  documentKind: "providerInvoice" | "buyInvoice";
};

const ProviderRecibeView = () => {
  const params = useParams();
  const modelRef = useRef<ModelIndexHandle>(null);
  const { providerRecibeId } = params;
  const [providerRecibe, setProviderRecibe] = useState<ProviderRecibeDataType>({})
  const [isLoading, setIsLoading] = useState<boolean>(false);

  useEffect(() => {
    if (providerRecibeId) {
      const run = async (providerRecibeId: string) => {
        try {
          const recibe = await ProviderRecibeApi.get(providerRecibeId, {
            selectionSet: [
              ...ProviderRecibeFormDataKeys.filter(k => !ProviderRecibeRelatedAttributes.includes(k)),
              "currency.*",
              "provider.*",
              "bankAccount.*",
              "creditOperator.*",
              "providerInvoices.*",
              "providerInvoices.currency.*",
            ]
          });
          setProviderRecibe(recibe);
          setTimeout(() => {
            modelRef.current?.refetch()
          });
        } finally {
          setIsLoading(false)
        }
      }
      run(providerRecibeId);
      setIsLoading(true)
    }
  }, [providerRecibeId]);

  const getProviderInvoices = async (): Promise<FetchResponse<ProviderRecibeDocumentRow>> => {
    const providerInvoices: ProviderRecibeDocumentRow[] = (providerRecibe.providerInvoices || []).map((invoice) => ({
      ...invoice,
      documentKind: "providerInvoice" as const,
    }));

    const { items: buyInvoices = [] } = providerRecibeId
      ? await BuyInvoiceApi.fetchAllByRecibe(`${providerRecibeId}`)
      : { items: [] };

    const buyInvoiceRows: ProviderRecibeDocumentRow[] = buyInvoices.map((invoice) => ({
      ...invoice,
      documentKind: "buyInvoice" as const,
    }));

    return {
      items: [...providerInvoices, ...buyInvoiceRows],
    };
  }

  return (
    <PageContainer
      title="Detalle Recibo de Pago Provedor"
      backUrl="/user/provider-invoices"
    >
      <Container fluid>
        <Row>
          <Col xs="2" md="2">
            <h5><b>Provedor:</b></h5>
          </Col>
          <Col>
            <h5><b>{providerRecibe?.provider?.nombre}</b></h5>
          </Col>
        </Row>
        <Row>
          <Col xs="2" md="2">
            <b>Fecha:</b>
          </Col>
          <Col>
            {formatDate(providerRecibe?.created)}
          </Col>
        </Row>
        <Row>
          <Col xs="2" md="2">
            <b>Numero Recibo:</b>
          </Col>
          <Col>
            {providerRecibe?.numero_recibo}
          </Col>
        </Row>
        <Row>
          <Col xs="2" md="2">
            <b>Forma Pago:</b>
          </Col>
          <Col>
            {PAYMENT_METHODS[Number(`${providerRecibe?.forma_pago}` || 1)]?.label || ""}
          </Col>
        </Row>
        <Row>
          <Col xs="2" md="2">
            <b>Referencia:</b>
          </Col>
          <Col>
            {providerRecibe?.reference || ""}
          </Col>
        </Row>
        <Row>
          <Col xs="2" md="2">
            <b>Banco:</b>
          </Col>
          <Col>
            {providerRecibe?.bankAccount?.nombre || ""}
          </Col>
        </Row>
        <Row className="mt-2">
          <Col xs="2" md="2">
            <h5><b>Saldo Cancelado:</b></h5>
          </Col>
          <Col>
            <h5><b>{formatMoneyValue(providerRecibe?.total, providerRecibe?.currency?.currency_id)}</b></h5>
          </Col>
        </Row>
        {/* Difiere del saldo cuando el recibo dejo un abono parcial sobre una de las facturas. */}
        <Row>
          <Col xs="2" md="2">
            <h5><b>Monto Pagado:</b></h5>
          </Col>
          <Col>
            <h5><b>{formatMoneyValue(providerRecibe?.abono, providerRecibe?.currency?.currency_id)}</b></h5>
          </Col>
        </Row>
        <Row>
          <Col xs="2" md="2">
            <b>Comentarios:</b>
          </Col>
          <Col>
            {stripHtmlTags(`${providerRecibe.comments || ""}`)}
          </Col>
        </Row>
        <hr />
      </Container>
      <ModelIndex<ProviderRecibeDocumentRow>
        messages={{
          noResults: "No hay facturas asignadas a este recibo"
        }}
        ref={modelRef}
        fetchData={() => getProviderInvoices()}
        primaryProperties={[
          {
            key: (d) => d.documentKind === "buyInvoice" ? `FEC-${d.numero}` : d.sequence,
            highlighted: true
          },
          {
            key: (d) => providerRecibe.provider?.nombre,
            alignment: "Start"
          },
        ]}
        primaryPropertiesSizing={
          [
            { sm: "4", xs: "4" },
            { sm: "6", xs: "8" }
          ]
        }
        secondaryProperties={[
          {
            key: (data) => {
              return formatDate(data.createdAt)
            },
            title: "Fecha",
          },
          {
            key: (d) => d.tipo == "0" ? "Contado" : "Credito",
            title: "Tipo Factura",
          },
          {
            key: "status",
            title: "Estado",
          },
          {
            key: (data) => {
              const currencyId = data.currency?.currency_id ?? providerRecibe.currency?.currency_id;
              return formatMoneyValue(data.total, currencyId);
            },
            title: "Total",
            highlighted: true
          }
        ]}
        secondaryPropertiesSizing={
          [
            { sm: "3", md: "2" },
            { sm: "3", md: "2" },
            { sm: "3", md: "2" },
            { sm: "3", md: "2" }
          ]
        }
      >
      </ModelIndex>
      <hr />
      <LoadingScreen isLoading={isLoading} />
    </PageContainer >
  );
}

export default ProviderRecibeView;
